'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import {
  flagValuesFromBootstrap,
  isLocalLaunchDarklyBypassEnabled,
  type LaunchDarklyBootstrap,
  type LDAppFlags,
} from 'src/config/LDFeatureFlags';
import {
  EMPTY_LAUNCH_DARKLY_FLAGS,
  LaunchDarklyBootstrappedContext,
  LaunchDarklyFlagsContext,
} from 'src/context/LaunchDarklyFlagsContext';
import { logger } from 'src/helper/logger';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { setLdIdentified } from 'src/redux/profile/slice';

// APX-303: the LaunchDarkly client SDK (~240 KB) was statically imported here and
// in `useFeatureFlags`, so it shipped in the up-front bundle for every route.
// It now loads as its own chunk, off the critical path. `ssr: false` because the
// SDK is browser-only and produced no flags server-side anyway.
//
// The import starts as soon as this provider mounts rather than being deferred to
// idle: flag-gated route layouts (`scheduling`, `quotes`, the settings
// pages) hold a skeleton until `state.profile.ldIdentified` flips — and
// fall through to the "settings unavailable" card if it never does (APX-382) — so
// delaying the load would delay their first paint by exactly that much.
const LaunchDarklyClientBridge = dynamic(() => import('./LaunchDarklyClientBridge'), {
  ssr: false,
});

/**
 * Values-only comparison, so a fresh object carrying identical flags does not
 * re-render every consumer of the context. The SDK hands `useFlags()` a new
 * object on some updates even when nothing changed.
 */
const haveSameFlagValues = (previous: LDAppFlags, next: LDAppFlags): boolean => {
  const previousKeys = Object.keys(previous) as (keyof LDAppFlags)[];
  const nextKeys = Object.keys(next);
  if (previousKeys.length !== nextKeys.length) return false;
  return previousKeys.every((flagKey) => previous[flagKey] === next[flagKey]);
};

function LDBypassIdentifier({ isMissingClientId }: { isMissingClientId: boolean }) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Otherwise a misconfigured env silently resolves every flag off.
    if (isMissingClientId) {
      logger.error(
        '[LaunchDarkly] NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID is not set; flags resolve off',
      );
    }
    dispatch(setLdIdentified());
  }, [dispatch, isMissingClientId]);

  return null;
}

export function LaunchDarklyProvider({
  children,
  bootstrap = null,
}: {
  children: React.ReactNode;
  bootstrap?: LaunchDarklyBootstrap | null;
}) {
  const clientSideID = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID;
  const isBypassed = isLocalLaunchDarklyBypassEnabled() || !clientSideID;
  // Only trusted alongside a client that will stream updates for the same org.
  const activeBootstrap = isBypassed ? null : bootstrap;
  const [flags, setFlags] = useState<LDAppFlags>(() =>
    activeBootstrap ? flagValuesFromBootstrap(activeBootstrap.flags) : EMPTY_LAUNCH_DARKLY_FLAGS,
  );

  const handleFlagsChange = useCallback((nextFlags: LDAppFlags) => {
    setFlags((previousFlags) =>
      haveSameFlagValues(previousFlags, nextFlags) ? previousFlags : nextFlags,
    );
  }, []);

  // The context provider wraps `children` unconditionally — on the bypass path,
  // while the bridge chunk is in flight, and after it lands. Consumers therefore
  // never change position in the tree, and flags simply go from empty to
  // populated, which is the same transition they saw while `LDProvider` was
  // still initializing its client.
  return (
    <LaunchDarklyBootstrappedContext.Provider value={Boolean(activeBootstrap)}>
      <LaunchDarklyFlagsContext.Provider value={flags}>
        {isBypassed ? (
          <LDBypassIdentifier
            isMissingClientId={!clientSideID && !isLocalLaunchDarklyBypassEnabled()}
          />
        ) : (
          <LaunchDarklyClientBridge
            clientSideID={clientSideID}
            bootstrap={activeBootstrap}
            onFlagsChange={handleFlagsChange}
          />
        )}
        {children}
      </LaunchDarklyFlagsContext.Provider>
    </LaunchDarklyBootstrappedContext.Provider>
  );
}
